/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { TextField, Button, Checkbox } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { useEffect, useState } from "react";
import { isString, isValidInput } from "../../utils/validations";
import { showError, showToast } from "../../constants/toasts";
import { ROLES } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";
import {
  RoleResponse,
  useAddRoleMutation,
  useUpdateRoleByIdMutation,
} from "../../services/accessRole";
import useTranslation from "../../hooks/traslation";

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state }: { state: RoleResponse } = useLocation();
  const [roleName, setRoleName] = useState("");
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleByIdMutation();
  const [error, setError] = useState(false);
  const user = useAuth();
  const translation = useTranslation();
  const [rows, setRows] = useState([
    {
      id: 1,
      name: "Dashboard",
      view: true,
      add: true,
      edit: true,
      delete: true,
    },
    {
      id: 2,
      name: "Manage Employers",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 3,
      name: "Manage Freelancers",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 4,
      name: "Manage Categories",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 5,
      name: "Manage Tasks",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 6,
      name: "Manage Payout",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 7,
      name: "Manage Wallet",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 8,
      name: "Manage Refunds",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 9,
      name: "Manage Payments",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 11,
      name: "Manage Notifications",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 12,
      name: "Manage Reviews",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
    {
      id: 13,
      name: "Manage CMS",
      view: false,
      add: false,
      edit: false,
      delete: false,
    },
  ]);

  // Handle checkbox toggle
  const handleCheckboxChange = (
    id: number,
    field: keyof (typeof rows)[number]
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? row.name === "Dashboard" && field !== "view"
            ? { ...row, [field]: true }
            : { ...row, [field]: !row[field] }
          : row
      )
    );
  };
  const handleSelectAll = (field: keyof (typeof rows)[number]) => {
    const allSelected = rows.every((row) => row[field]);
    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        [field]: !allSelected,
        // If selecting view, also select add and delete
        ...(field === "view" && !allSelected
          ? { add: true, delete: true }
          : {}),
        // If deselecting view, also deselect add and delete
        ...(field === "view" && allSelected
          ? { add: false, delete: false }
          : {}),
      }))
    );
  };
  const handleAddEditRole = async () => {
    if (!roleName) {
      return;
    }

    const hasPermission = rows.some(
      (el) =>
        el.name !== "Dashboard" && (el.view || el.add || el.edit || el.delete)
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (!hasPermission) {
      showError("Please select at least one permission other than Dashboard");
      return;
    }

    const body = {
      name: roleName,
      permission: rows.map((item) => ({
        sideBarId: item.id,
        label: item.name,
        isView: item.view,
        isAdd: item.add,
        isEdit: item.add,
        isDelete: item.delete,
      })),
    };

    try {
      let response;
      if (id) {
        // If ID exists, update customer
        response = await updateRole({ id, body }).unwrap();
      } else {
        // Else, add customer
        response = await addRole({ body }).unwrap();
      }
      if (response?.statusCode === 200) {
        // setUserId(response?.data?._id || "");
        showToast(
          id ? translation.error.roleUUpdate : translation.error.addAdded
        );

        navigate("/manage-subadmin/roles");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      console.log(error);
    }
  };
  const handleRoleNameChange = (i: string) => {
    setRoleName(i);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "name",
      headerName: translation.main.moduleName,
      editable: false,
      sortable: false,
    },
    {
      flex: 1,
      minWidth: 180,
      field: "view",
      headerName: translation.globals.View,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "0 8px"
          }}
        >
          <Checkbox
            checked={rows.every((row) => row.view)}
            indeterminate={
              rows.some((row) => row.view) && !rows.every((row) => row.view)
            }
            onChange={() => handleSelectAll("view")}
            size="small"
          />
          <span style={{ fontSize: "14px", fontWeight: 500 }}>{translation.globals.View}</span>
        </div>
      ),
      renderCell: ({ row }) => (
        <Checkbox
          disabled={row.name === "Dashboard"}
          checked={row.view}
          onChange={() => {
            if (row.view) {
              if (row.add) {
                handleCheckboxChange(row.id, "add");
              }
              if (row.delete) {
                handleCheckboxChange(row.id, "delete");
              }
            }
            handleCheckboxChange(row.id, "view");
          }}
        />
      ),
    },
    {
      flex: 1,
      minWidth: 180,
      field: "add",
      headerName: translation.main.addEdit,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "0 8px"
          }}
        >
          <Checkbox
            checked={rows.every((row) => row.add)}
            indeterminate={
              rows.some((row) => row.add) && !rows.every((row) => row.add)
            }
            onChange={() => handleSelectAll("add")}
            size="small"
          />
          <span style={{ fontSize: "14px", fontWeight: 500 }}>{translation.main.addEdit}</span>
        </div>
      ),
      renderCell: ({ row }) => (
        <Checkbox
          disabled={row.name === "Dashboard"}
          checked={row.add}
          onChange={() => {
            if (!row.view) {
              handleCheckboxChange(row.id, "view");
            }
            handleCheckboxChange(row.id, "add");
          }}
        />
      ),
    },
    {
      flex: 1,
      minWidth: 100,
      field: "delete",
      headerName: translation.globals.Delete,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "0 8px"
          }}
        >
          <Checkbox
            checked={rows.every((row) => row.delete)}
            indeterminate={
              rows.some((row) => row.delete) && !rows.every((row) => row.delete)
            }
            onChange={() => handleSelectAll("delete")}
            size="small"
          />
          <span style={{ fontSize: "14px", fontWeight: 500 }}>{translation.globals.Delete}</span>
        </div>
      ),
      renderCell: ({ row }) => (
        <Checkbox
          checked={row.delete}
          disabled={row.name === "Dashboard"}
          onChange={() => {
            if (!row.view) {
              handleCheckboxChange(row.id, "view");
            }
            handleCheckboxChange(row.id, "delete");
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    if (user?.role === ROLES.SUB_ADMIN) {
      navigate("/permission-denied", { replace: true });
    }
  }, [user]);

  useEffect(() => {
    if (id) {
      setRoleName(state?.name);
      setRows([
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...state?.permission.map((item: any, i: number) => ({
          name: item?.label,
          id: i + 1,
          view: item?.isView,
          add: item?.isAdd,
          edit: item?.isEdit,
          delete: item?.isDelete,
        })),
      ]);
    }
  }, [id]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? translation.main.editRole : translation.main.addRole}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translation.dashboard.dashboard}
          </span>{" "}
          -{" "}
          <span onClick={() => navigate("/manage-subadmin")}>
            {translation.main.manageSubadmin}
          </span>{" "}
          -{" "}
          <span onClick={() => navigate("/manage-subadmin/roles")}>
            {translation.main.mangeRoles}
          </span>{" "}
          - {id ? translation.main.editRole : translation.main.addRole}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label>{translation.main.roleName}</label>
              <TextField
                placeholder={translation.main.roleName}
                variant="outlined"
                className="text_field"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                inputProps={{ maxLength: 30 }}
                value={roleName}
                // disabled={Boolean(id)}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    handleRoleNameChange(val.target.value);
                  }
                }}
              />
              {!roleName && error ? (
                <h6 className="err_msg">{translation.error.roleNameReq}</h6>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="sorting_table mt_20 role">
            <TableComponent
              columns={columns}
              rows={rows}
              onPageChange={() => {}}
            />
          </div>
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setError(true);
                handleAddEditRole();
              }}
            >
              {translation.globals.save}
            </Button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddRole;
